import { Bell, LayoutDashboard, List, LogOut, Plus, Settings as SettingsIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/cn'
import { Button } from './ui'

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/items', label: 'All records', icon: List },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
] as const

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-dvh">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-semibold text-ink">
            <Bell className="size-5 text-brand" aria-hidden="true" />
            Reminded
          </Link>

          <nav className="ml-2 flex items-center gap-1" aria-label="Main">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm',
                    isActive
                      ? 'bg-brand-soft font-medium text-brand'
                      : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                  )
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => navigate('/items/new')} className="whitespace-nowrap">
              <Plus className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Add record</span>
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              title={user ? `Sign out ${user.email}` : 'Sign out'}
              aria-label="Sign out"
            >
              <LogOut className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  )
}
